import * as R from 'ramda';
import { isEmpty } from 'ramda';
import { isNotEmpty } from 'ramda-adjunct';
import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';

import Banner from 'components/lib/ui/Banner';
import FlexContainer from 'components/lib/ui/FlexContainer';
import SearchBar, { Input } from 'components/lib/ui/SearchBar';
import Skeleton from 'components/lib/ui/Skeleton';
import FilterMenuColumn from 'components/reports/filters/FilterMenuColumn';
import FilterMenuEmpty from 'components/reports/filters/FilterMenuEmpty';
import FilterMenuOptionRow from 'components/reports/filters/FilterMenuOptionRow';
import FilterMenuOptionsList from 'components/reports/filters/FilterMenuOptionsList';
import FilterMenuSearchResults from 'components/reports/filters/FilterMenuSearchResults';
import type {
  FilterMenuOption,
  FilterMenuOptionInput,
  FilterMenuSection,
  WithPath,
} from 'components/reports/filters/types';
import { areAllChildrenSelected } from 'components/reports/filters/utils';

import { color, fontSize, spacing } from 'common/lib/theme/dynamic';
import HighlightTextContext from 'lib/contexts/HighlightTextContext';

const OptionsListContainer = styled(FlexContainer).attrs({ column: true })`
  padding: ${spacing.xsmall} ${spacing.small};
  gap: ${spacing.xxxsmall};
`;

const StyledSearchBar = styled(SearchBar)`
  width: 100%;

  ${Input} {
    border: none !important;
    box-shadow: none !important;
    font-size: ${fontSize.small};
    outline: none;

    :disabled {
      background-color: ${color.white};
    }
  }
`;

const SkeletonContainer = styled.div`
  flex: 1;
  display: flex;
  flex-flow: column;
  padding: ${spacing.xsmall} ${spacing.small};
  padding-top: ${spacing.small};
`;

const OptionListSkeletonContainer = styled(SkeletonContainer)`
  padding-left: 0;
  padding-top: 0;
  padding-right: ${spacing.small};
`;

const BaseSkeleton = styled(Skeleton)<{ $indent?: boolean }>`
  height: ${fontSize.small};

  &:not(:last-child) {
    margin-bottom: ${spacing.small};
  }

  ${({ $indent }) =>
    $indent &&
    css`
      margin-left: ${spacing.default};
    `}
`;

const SmallBanner = styled(Banner)`
  padding: ${spacing.xxsmall} ${spacing.xsmall};
  font-size: ${fontSize.small};
`;

export type OnClickApplyOptions = Partial<{ reset: boolean }>;

export type Props = {
  isLoading?: boolean;
  isSearchLoading?: boolean;
  onSearchChange: (text: string) => void;
  search: string;
  searchResults: (WithPath<FilterMenuSection> & {
    options: WithPath<FilterMenuOption>[];
  })[];
  searchRef: React.RefObject<HTMLInputElement>;
  validationErrors: string[];
  activeSection: FilterMenuSection;
  changeSelected: (path: string[], selected: boolean) => void;
  handleInputChange: (
    path: string[],
    optionId: string,
    input: FilterMenuOptionInput,
    value: unknown,
  ) => void;
  hasActiveSearch: boolean;
};

/**
 * This component is used to display the middle column of the FilterMenu which contains the search and the list of results.
 * The search is not limited to the active section.
 * If there is no active search, the results will be the full list of options for the active section.
 */
const FilterMenuSearch = ({
  isLoading,
  search,
  isSearchLoading,
  onSearchChange,
  searchResults,
  searchRef,
  validationErrors,
  activeSection,
  changeSelected,
  handleInputChange,
  hasActiveSearch,
}: Props) => {
  const activeSectionOptions = activeSection.options;
  const isSearchDisabled = isLoading || (isEmpty(search) && isEmpty(searchResults));

  const showLoadingState =
    isLoading ||
    (isSearchLoading &&
      search.length > 0 &&
      isEmpty(activeSection.options) &&
      isEmpty(searchResults));

  const sectionHasSubOptions = useMemo(
    () => activeSection.options.some((o) => o.options),
    [activeSection],
  );

  const sectionHasRadio = useMemo(
    () => activeSection.options.some(({ type }) => type === 'radio'),
    [activeSection],
  );

  const showSelectAll =
    isEmpty(search) &&
    isNotEmpty(activeSectionOptions) &&
    !sectionHasRadio &&
    !activeSection.hideSelectAll;

  return (
    <>
      <FilterMenuColumn
        header={
          <StyledSearchBar
            name="search"
            value={search}
            onChange={onSearchChange}
            placeholder="Search..."
            disabled={isSearchDisabled}
            ref={searchRef}
            autoFocus
            small
          />
        }
      >
        <OptionsListContainer>
          {showLoadingState ? (
            <OptionListSkeletonContainer>
              {R.range(0, 6).map((i) => (
                <BaseSkeleton key={i} $indent={i > 0 && i % 2 === 0} />
              ))}
            </OptionListSkeletonContainer>
          ) : (
            <>
              {showSelectAll && (
                <FilterMenuOptionRow
                  name="Select all"
                  isGroup
                  isSelected={areAllChildrenSelected(activeSection.options)}
                  onChangeSelected={(selected) => changeSelected([activeSection.id], selected)}
                />
              )}

              {isEmpty(searchResults) ? (
                <FilterMenuEmpty />
              ) : (
                <>
                  {validationErrors.length > 0 && (
                    <SmallBanner type="error">{validationErrors.join('\n')}</SmallBanner>
                  )}
                  <HighlightTextContext.Provider
                    value={{
                      searchWords: search.split(' '),
                    }}
                  >
                    {hasActiveSearch ? (
                      <FilterMenuSearchResults
                        sections={searchResults}
                        onChangeSelected={changeSelected}
                        onInputChange={handleInputChange}
                      />
                    ) : (
                      <FilterMenuOptionsList
                        flat={!!search || !sectionHasSubOptions}
                        onChangeSelected={changeSelected}
                        onInputChange={handleInputChange}
                        options={activeSectionOptions}
                      />
                    )}
                  </HighlightTextContext.Provider>
                </>
              )}
            </>
          )}
        </OptionsListContainer>
      </FilterMenuColumn>
    </>
  );
};

export default FilterMenuSearch;
